import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { useDroppable } from '@dnd-kit/core';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { COPY, pageFormatLabel } from '../presentationCopy';
import { PRES, TYPE } from '../chrome';
import { listLibraryLabels } from '../api';
import { PresentationLibraryLabel, PresentationLibrarySlide } from '../types';
import {
  filterLibrarySlides,
  filterLibrarySlidesByLabel,
  libraryPreviewSlide,
  librarySlideLabelNames,
  librarySlidesMatchingPageFormat,
} from '../librarySlides';
import { focusVisibleSx } from '../ui/focus';
import StaticSlideView from './StaticSlideView';
import { CARD_GAP, CARD_WIDTH, columnsForWidth } from './assetBrowserLayout';
import { SLIDE_LIBRARY_PANEL_DROP_ID } from './slideLibraryDrop';

interface LibraryPickerPanelProps {
  items: PresentationLibrarySlide[];
  pageFormat?: string | null;
  panelWidth: number;
  busy?: boolean;
  disabled?: boolean;
  canSaveToLibrary?: boolean;
  onAddPage: (librarySlideId: string) => void;
}

const LibraryPickerPanel: React.FC<LibraryPickerPanelProps> = ({
  items,
  pageFormat,
  panelWidth,
  busy,
  disabled,
  canSaveToLibrary = false,
  onAddPage,
}) => {
  const [query, setQuery] = useState('');
  const [labels, setLabels] = useState<PresentationLibraryLabel[]>([]);
  const [labelFilter, setLabelFilter] = useState('');
  const columns = columnsForWidth(panelWidth);
  const locked = Boolean(disabled || busy);
  const { setNodeRef, isOver } = useDroppable({
    id: SLIDE_LIBRARY_PANEL_DROP_ID,
    disabled: !canSaveToLibrary,
  });
  useEffect(() => {
    void listLibraryLabels().then(setLabels).catch(() => setLabels([]));
  }, []);
  const matchingFormat = useMemo(
    () => librarySlidesMatchingPageFormat(items.filter((item) => item.isActive !== false), pageFormat),
    [items, pageFormat],
  );
  const filtered = useMemo(
    () => filterLibrarySlides(filterLibrarySlidesByLabel(matchingFormat, labelFilter, labels), query, labels),
    [labelFilter, labels, matchingFormat, query],
  );

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        outline: isOver ? `2px solid ${PRES.interactive}` : 'none',
        outlineOffset: -2,
        bgcolor: isOver ? 'rgba(36,93,193,0.06)' : 'transparent',
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
          {COPY.slideLibrary}
        </Typography>
        <Typography sx={{ mt: 0.5, fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary }}>
          {COPY.libraryPickerHelp}
        </Typography>
        {canSaveToLibrary && (
          <Typography sx={{ mt: 0.5, fontFamily: TYPE.ui, ...TYPE.caption, color: isOver ? PRES.interactive : PRES.textTertiary }}>
            {COPY.libraryDropHelp}
          </Typography>
        )}
      </Box>
      {!!labels.length && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mx: 1.5, mb: 0.5 }}>
          <Button
            size="small"
            variant={!labelFilter ? 'contained' : 'outlined'}
            onClick={() => setLabelFilter('')}
            sx={{
              textTransform: 'none',
              boxShadow: 'none',
              minWidth: 0,
              bgcolor: !labelFilter ? PRES.interactive : PRES.surface,
              color: !labelFilter ? PRES.white : PRES.textPrimary,
              borderColor: PRES.borderSubtle,
            }}
          >
            {COPY.libraryGroupAll}
          </Button>
          {labels.map((label) => (
            <Button
              key={label._id}
              size="small"
              variant={labelFilter === label._id ? 'contained' : 'outlined'}
              onClick={() => setLabelFilter(label._id)}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                minWidth: 0,
                bgcolor: labelFilter === label._id ? PRES.interactive : PRES.surface,
                color: labelFilter === label._id ? PRES.white : PRES.textPrimary,
                borderColor: PRES.borderSubtle,
              }}
            >
              {label.name}
            </Button>
          ))}
        </Box>
      )}
      <TextField
        size="small"
        placeholder={COPY.searchLibrarySlides}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mx: 1.5, my: 1 }}
      />
      <Box sx={{ flex: 1, overflowX: 'hidden', overflowY: 'auto', px: 1.5, pb: 2 }}>
        {!matchingFormat.length && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.libraryPickerFormatEmpty}
          </Typography>
        )}
        {!!matchingFormat.length && !filtered.length && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.noLibrarySlideMatches}
          </Typography>
        )}
        {!!filtered.length && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, ${CARD_WIDTH}px)`,
            gap: `${CARD_GAP}px`,
            justifyContent: 'start',
          }}>
            {filtered.map((item) => {
              const labelText = librarySlideLabelNames(item, labels).join(', ');
              return (
                <Box
                  key={item._id}
                  component="button"
                  type="button"
                  disabled={locked}
                  onClick={() => onAddPage(item._id)}
                  sx={{
                    width: CARD_WIDTH,
                    p: 0,
                    m: 0,
                    textAlign: 'left',
                    border: `1px solid ${PRES.borderSubtle}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: locked ? 'default' : 'pointer',
                    bgcolor: PRES.surface,
                    color: 'inherit',
                    font: 'inherit',
                    opacity: locked ? 0.64 : 1,
                    position: 'relative',
                    '&:hover:not(:disabled)': { borderColor: PRES.interactive },
                    '&:hover:not(:disabled) .layout-add, &:focus-within .layout-add': { opacity: 1 },
                    ...focusVisibleSx,
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <StaticSlideView
                      pageFormat={item.pageFormat || pageFormat}
                      slide={libraryPreviewSlide(item)}
                    />
                    <IconButton
                      className="layout-add"
                      size="small"
                      disabled={locked}
                      title={COPY.addSlide}
                      aria-label={COPY.addSlide}
                      onClick={(event) => {
                        event.stopPropagation();
                        onAddPage(item._id);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        opacity: 0,
                        bgcolor: PRES.interactive,
                        color: PRES.white,
                        width: 28,
                        height: 28,
                        '&:hover': { bgcolor: PRES.interactiveHover },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  <Typography sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 32,
                    px: 0.75,
                    pt: 0.75,
                    fontFamily: TYPE.ui,
                    ...TYPE.caption,
                    fontWeight: 650,
                    color: PRES.textPrimary,
                  }}>
                    {item.name}
                  </Typography>
                  <Typography noWrap sx={{
                    display: 'block',
                    px: 0.75,
                    pb: 0.75,
                    minHeight: 18,
                    fontFamily: TYPE.ui,
                    ...TYPE.caption,
                    color: PRES.textTertiary,
                  }}>
                    {pageFormatLabel(item.pageFormat)}
                    {labelText ? ` · ${labelText}` : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LibraryPickerPanel;
